// src/shared/components/ui/Toast/ToastContainer.tsx
import React from 'react';
import { Box, IconButton, Snackbar, Modal, Button, Typography } from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useToastStore } from '../../../../app/store/useToastStore';
import theme from '../../../styles/theme/theme';
import { colors } from '../../../styles/theme/colors';
import { useAuth } from '../../../contexts/AuthContext';

const ToastContainer: React.FC = () => {
  const { toasts, removeToast } = useToastStore();
  const { userRole } = useAuth();
  const { t } = useTranslation();

  // Get primary color based on user role
  const getPrimaryColor = () => {
    if (userRole === 'traveller') return colors.traveller.primary;
    if (userRole === 'sender') return colors.sender.primary;
    if (userRole === 'receiver') return colors.receiver.primary;
    return colors.generic.primary;
  };

  const getIcon = (type: string) => {
    switch (type) {
      case 'success':
      case 'kycVerified':
        return <CheckCircleIcon sx={{ fontSize: 20 }} />;
      case 'error':
      case 'kycCanceled':
      case 'kycRestricted':
        return <ErrorIcon sx={{ fontSize: 20 }} />;
      case 'warning':
      case 'kycPending':
      case 'kycCardPending':
      case 'kycRequiresInput':
      case 'kycProcessing':
      case 'payoutPending':
      case 'payoutBankPending':
      case 'payoutManualReview':
        return <WarningIcon sx={{ fontSize: 20 }} />;
      case 'payoutActive':
        return <CheckCircleIcon sx={{ fontSize: 20 }} />;
      case 'payoutDeclined':
        return <ErrorIcon sx={{ fontSize: 20 }} />;
      case 'info':
      default:
        return <InfoIcon sx={{ fontSize: 20 }} />;
    }
  };

  const getColors = (type: string) => {
    switch (type) {
      case 'success':
        return {
          background: '#DCFCE7',
          border: '#86EFAC',
          iconColor: colors.semantic.success,
          textColor: '#15803D',
        };
      case 'error':
        return {
          background: '#FEE2E2',
          border: '#FECACA',
          iconColor: colors.semantic.error,
          textColor: '#991B1B',
        };
      case 'warning':
        return {
          background: '#FEF3C7',
          border: '#FDE68A',
          iconColor: colors.semantic.warning,
          textColor: '#92400E',
        };
      case 'kycVerified':
        return {
          background: '#E9F7EE',
          border: '#1FB356',
          iconColor: '#1FB356',
          textColor: '#5D606D',
        };
      case 'kycPending':
      case 'kycCardPending':
        return {
          background: '#FEF3C7',
          border: '#FDE68A',
          iconColor: '#A57B00',
          textColor: '#92400E',
        };
      case 'kycCanceled':
      case 'kycRestricted':
        return {
          background: '#FDEAEA',
          border: '#E62E2E',
          iconColor: '#E62E2E',
          textColor: '#5D606D',
        };
      case 'kycRequiresInput':
        return {
          background: '#F5E8EF',
          border: '#9B1B5A',
          iconColor: '#9B1B5A',
          textColor: '#5D606D',
        };
      case 'kycProcessing':
        return {
          background: '#F5E8EF',
          border: '#9B1B5A',
          iconColor: '#9B1B5A',
          textColor: '#5D606D',
        };
      case 'payoutPending':
      case 'payoutBankPending':
        return {
          background: colors.payoutBanner.orange.background,
          border: colors.payoutBanner.orange.border,
          iconColor: colors.payoutBanner.orange.icon,
          textColor: colors.payoutBanner.bodyColor,
        };
      case 'payoutActive':
        return {
          background: colors.payoutBanner.success.background,
          border: colors.payoutBanner.success.border,
          iconColor: colors.payoutBanner.success.icon,
          textColor: colors.payoutBanner.bodyColor,
        };
      case 'payoutDeclined':
        return {
          background: colors.payoutBanner.error.background,
          border: colors.payoutBanner.error.border,
          iconColor: colors.payoutBanner.error.icon,
          textColor: colors.payoutBanner.bodyColor,
        };
      case 'payoutManualReview':
        return {
          background: colors.payoutBanner.blue.background,
          border: colors.payoutBanner.blue.border,
          iconColor: colors.payoutBanner.blue.icon,
          textColor: colors.payoutBanner.bodyColor,
        };
      case 'info':
      default:
        return {
          background: '#FDF2F8',
          border: '#F9A8D4',
          iconColor: getPrimaryColor(),
          textColor: '#831843',
        };
    }
  };

  return (
    <>
      {toasts.map((toast, index) => {
        const toastColors = getColors(toast.type);

        // Render blocking toast as a modal
        if (toast.blocking) {
          return (
            <Modal
              key={toast.id}
              open={true}
              onClose={() => {}} // Prevent closing by clicking backdrop
              disableEnforceFocus={false}
              disableAutoFocus={false}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10000,
                backdropFilter: 'blur(8px)',
              }}
              slotProps={{
                backdrop: {
                  sx: {
                    backgroundColor: 'rgba(0, 0, 0, 0.2)',
                    backdropFilter: 'blur(8px)',
                  }
                }
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#FFFFFF',
                    borderRadius: '16px',
                    padding: { xs: '24px', sm: '32px' },
                    minWidth: { xs: '90vw', sm: '400px' },
                    maxWidth: { xs: '90vw', sm: '500px' },
                    boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.25)',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  {/* Background pattern decorative - Top left corner, partially visible */}
                  <Box
                    sx={{
                      position: 'absolute',
                      width: '280px',
                      height: '280px',
                      left: '-100px',
                      top: '-100px',
                      overflow: 'hidden',
                      pointerEvents: 'none',
                      zIndex: 0,
                    }}
                  >
                    {/* Concentric circles - reduced spacing */}
                    {[80, 120, 160, 200, 240, 280].map((size) => (
                      <Box
                        key={size}
                        sx={{
                          position: 'absolute',
                          width: `${size}px`,
                          height: `${size}px`,
                          left: `calc(50% - ${size / 2}px)`,
                          top: `calc(50% - ${size / 2}px)`,
                          border: '1px solid #edeef0ff',
                          borderRadius: '50%',
                        }}
                      />
                    ))}
                  </Box>

                  {/* Featured icon at top left */}
                  <Box
                    sx={{
                      position: 'relative',
                      left: { xs: '-14px', sm: '-14px' },
                      top: { xs: '-14px', sm: '-14px' },
                      width: { xs: '48px', sm: '48px' },
                      height: { xs: '48px', sm: '48px' },
                      background: toast.type === 'error' ? '#FEE2E2' : '#F2E6EE',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      zIndex: 1,
                      marginBottom: '24px',
                    }}
                  >
                    <Box
                      sx={{
                        color: toastColors.iconColor,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '20px',
                        height: '20px',
                        '& svg': {
                          fontSize: '20px !important',
                          width: '20px !important',
                          height: '20px !important'
                        }
                      }}
                    >
                      {getIcon(toast.type)}
                    </Box>
                  </Box>

                  {/* Close button */}
                  <IconButton
                    onClick={() => removeToast(toast.id)}
                    sx={{
                      position: 'absolute',
                      top: '16px',
                      right: '16px',
                      width: '32px',
                      height: '32px',
                      padding: '6px',
                      color: '#9B9CA6',
                      zIndex: 2,
                      '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                    }}
                    aria-label="Close"
                  >
                    <CloseIcon sx={{ fontSize: 20 }} />
                  </IconButton>

                  {/* Message content - center aligned */}
                  <Box sx={{ width: '100%', paddingRight: '32px', position: 'relative', zIndex: 1, textAlign: 'center' }}>
                    <Typography
                      sx={{
                        fontSize: { xs: '18px', sm: '20px' },
                        fontWeight: 600,
                        color: '#181D27',
                        fontFamily: theme.typography.fontFamily,
                        marginBottom: '8px',
                      }}
                    >
                      {toast.message}
                    </Typography>

                    {toast.secondaryMessage && (
                      <Typography
                        sx={{
                          fontSize: { xs: '13px', sm: '14px' },
                          fontWeight: 400,
                          color: '#535862',
                          fontFamily: theme.typography.fontFamily,
                          marginBottom: '16px',
                        }}
                      >
                        {toast.secondaryMessage}
                      </Typography>
                    )}

                    {/* Action link as greyish clickable text */}
                    {toast.action && (
                      <Box
                        onClick={() => {
                          toast.action?.onClick();
                          removeToast(toast.id);
                        }}
                        sx={{
                          cursor: 'pointer',
                          fontSize: '13px',
                          fontWeight: 500,
                          color: '#777B8B',
                          fontFamily: theme.typography.fontFamily,
                          textDecoration: 'underline',
                          display: 'inline-block',
                          marginBottom: '24px',
                          '&:hover': {
                            color: '#535862',
                          },
                        }}
                      >
                        {toast.action.label}
                      </Box>
                    )}

                    {/* Okay button */}
                    <Button
                      onClick={() => removeToast(toast.id)}
                      variant="contained"
                      sx={{
                        backgroundColor: getPrimaryColor(),
                        color: '#FFFFFF',
                        fontFamily: theme.typography.fontFamily,
                        fontSize: '14px',
                        fontWeight: 600,
                        textTransform: 'none',
                        borderRadius: '8px',
                        padding: '10px 24px',
                        width: '100%',
                        '&:hover': {
                          backgroundColor: getPrimaryColor(),
                          opacity: 0.9,
                        },
                      }}
                    >
                      {t('common.okay')}
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Modal>
          );
        }

        // Render normal toast as Snackbar
        const isTopCenter = toast.position === 'top-center';
        return (
          <Snackbar
            key={toast.id}
            open={true}
            anchorOrigin={{ vertical: 'top', horizontal: isTopCenter ? 'center' : 'right' }}
            sx={isTopCenter
              ? {
                  top: { xs: `${8 + index * 70}px`, sm: `${8 + index * 70}px` },
                  zIndex: 10000, // Higher than modal backdrop (9999)
                }
              : {
                  top: { xs: `${80 + index * 70}px`, sm: `${100 + index * 70}px` },
                  right: { xs: '16px', sm: '24px' },
                  zIndex: 10000, // Higher than modal backdrop (9999)
                }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                backgroundColor: toastColors.background,
                border: `1px solid ${toastColors.border}`,
                borderRadius: '12px',
                padding: { xs: '12px 16px', sm: '14px 20px' },
                minWidth: { xs: 'min(280px, 90vw)', sm: '320px' },
                maxWidth: toast.wide ? { xs: '90vw', sm: '700px' } : { xs: '90vw', sm: '400px' },
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                backdropFilter: 'blur(16px)',
              }}
            >
              <Box
                sx={{
                  color: toastColors.iconColor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {getIcon(toast.type)}
              </Box>

              <Box
                sx={{
                  flex: 1,
                  fontSize: { xs: '14px', sm: '15px' },
                  fontWeight: 500,
                  color: toastColors.textColor,
                  fontFamily: theme.typography.fontFamily,
                  lineHeight: '20px',
                }}
              >
                {toast.message}
              </Box>

              <IconButton
                onClick={() => removeToast(toast.id)}
                sx={{
                  width: '32px',
                  height: '32px',
                  padding: '6px',
                  borderRadius: '6px',
                  color: toastColors.iconColor,
                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                }}
                aria-label="Close"
              >
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          </Snackbar>
        );
      })}
    </>
  );
};

export default ToastContainer;
